import React, { useState, useEffect, useRef, useCallback, useMemo, memo, Suspense, lazy } from 'react';
import { Link, NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import API from '../api';
import Logo from './Logo';
import Toast from './Toast';
import AIDiscoveryBar from './AIDiscoveryBar';
import { clearStoredGoogleSession } from '../utils/googleAuth';
import './NavBar.css';
import './NavBar-favorites.css';
// Import the countries data
import { countries } from '../utils/countries';

// Market options for the toggleable flag dropdown
const MARKETS = [
  {
    id: 'global',
    label: 'Global Marketplace',
    description: 'Values-driven discovery worldwide',
    flag: '🌍',
    path: '/global/home'
  },
  {
    id: 'canada',
    label: 'Canada',
    description: 'Canadian artisans & makers',
    flag: 'https://flagcdn.com/ca.svg',
    path: '/country/canada'
  },
  {
    id: 'australia',
    label: 'Australia',
    description: 'Home robot guides for Australia',
    flag: 'https://flagcdn.com/au.svg',
    path: '/country/australia/blog'
  }
];

// Dynamically import the new GoogleSignInButton component
const GoogleSignInButton = lazy(() => import('./GoogleSignInButton'));

const isReactSnap = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap'; // Check for ReactSnap

// Memoize the NavLink item to prevent re-renders when parent state changes
const MemoizedNavItem = memo(({ to, onClick, children, selectedCountry }) => (
  <li>
    <NavLink
      to={to}
      className={({ isActive }) => (isActive ? 'active' : '')}
      onClick={onClick}
    >
      {children}
    </NavLink>
  </li>
));

// Special BRANDS nav item with dropdown for global market
const BrandsNavItem = memo(({ onClick, currentMarket, selectedCountry }) => {
  const location = useLocation();
  const [showBrandsDropdown, setShowBrandsDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // Check if we're on any brands page
  const isBrandsActive = location.pathname.includes('/brands');
  
  // Determine the correct brands path based on current market
  const brandsPath = currentMarket === 'global' ? '/global/brands' : `/country/${selectedCountry}/brands`;
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowBrandsDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // For Canadian market, show dropdown with By Province and By Value
  if (currentMarket !== 'global') {
    return (
      <li className="nav-item-with-dropdown" ref={dropdownRef}>
        <div className="nav-dropdown-trigger"
          onMouseEnter={() => {
            // Prefetch brand page chunks when hovering over BRANDS nav item
            import('../pages/CanadianBrandsHub').catch(() => {});
          }}
        >
          <NavLink
            to={brandsPath}
            className={isBrandsActive ? 'active' : ''}
            onClick={(e) => {
              // On mobile, toggle dropdown instead of navigating
              const isMobile = window.innerWidth <= 768;
              if (isMobile) {
                e.preventDefault();
                setShowBrandsDropdown(!showBrandsDropdown);
              } else {
                onClick(e);
              }
            }}
          >
            BRANDS
          </NavLink>
          <button 
            className="dropdown-toggle-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowBrandsDropdown(!showBrandsDropdown);
              // Prefetch brand page chunks when dropdown opens
              if (!showBrandsDropdown) {
                import('../pages/CanadianBrandsHub').catch(() => {});
                import('../pages/CanadianBrandsByValues').catch(() => {});
              }
            }}
            aria-label="Toggle brands menu"
          >
            <span className="dropdown-arrow-small">▼</span>
          </button>
        </div>
        {showBrandsDropdown && (
          <div className="nav-dropdown-menu">
            <Link 
              to={`/country/${selectedCountry}/brands`}
              className="nav-dropdown-item"
              onClick={() => {
                setShowBrandsDropdown(false);
                onClick();
              }}
              onMouseEnter={() => {
                // Prefetch the CanadianBrandsHub chunk on hover
                import('../pages/CanadianBrandsHub').catch(() => {});
              }}
            >
              <span className="dropdown-item-icon">📍</span>
              By Province
            </Link>
            <Link 
              to={`/country/${selectedCountry}/brands/by-values`}
              className="nav-dropdown-item"
              onClick={() => {
                setShowBrandsDropdown(false);
                onClick();
              }}
              onMouseEnter={() => {
                // Prefetch the CanadianBrandsByValues chunk on hover
                import('../pages/CanadianBrandsByValues').catch(() => {});
              }}
            >
              <span className="dropdown-item-icon">💎</span>
              By Value
            </Link>
            <Link 
              to={`/country/${selectedCountry}/register`}
              className="nav-dropdown-item"
              onClick={() => {
                setShowBrandsDropdown(false);
                onClick();
              }}
            >
              <span className="dropdown-item-icon">✨</span>
              Add Your Brand
            </Link>
          </div>
        )}
      </li>
    );
  }
  
  // For global market, show dropdown
  return (
    <li className="nav-item-with-dropdown" ref={dropdownRef}>
      <div className="nav-dropdown-trigger">
        <NavLink
          to={brandsPath}
          className={isBrandsActive ? 'active' : ''}
          onClick={(e) => {
            // On mobile, toggle dropdown instead of navigating
            const isMobile = window.innerWidth <= 768;
            if (isMobile) {
              e.preventDefault();
              setShowBrandsDropdown(!showBrandsDropdown);
            } else {
              onClick(e);
            }
          }}
        >
          BRANDS
        </NavLink>
        <button 
          className="dropdown-toggle-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowBrandsDropdown(!showBrandsDropdown);
          }}
          aria-label="Toggle brands menu"
        >
          <span className="dropdown-arrow-small">▼</span>
        </button>
      </div>
      {showBrandsDropdown && (
        <div className="nav-dropdown-menu">
          <Link 
            to="/global/brands" 
            className="nav-dropdown-item"
            onClick={() => {
              setShowBrandsDropdown(false);
              onClick();
            }}
          >
            <span className="dropdown-item-icon">🏢</span>
            All Brands
          </Link>
          <Link 
            to="/global/brands/by-values" 
            className="nav-dropdown-item"
            onClick={() => {
              setShowBrandsDropdown(false);
              onClick();
            }}
          >
            <span className="dropdown-item-icon">💎</span>
            By Values
          </Link>
          <Link 
            to="/global/brands/by-location" 
            className="nav-dropdown-item"
            onClick={() => {
              setShowBrandsDropdown(false);
              onClick();
            }}
          >
            <span className="dropdown-item-icon">📍</span>
            By Location
          </Link>
          <Link 
            to="/global/register" 
            className="nav-dropdown-item"
            onClick={() => {
              setShowBrandsDropdown(false);
              onClick();
            }}
          >
            <span className="dropdown-item-icon">✨</span>
            Add Your Brand
          </Link>
        </div>
      )}
    </li>
  );
});

// Special MORE nav item with dropdown for Blog, Gifts, Collections, My Values, Top Searches
// showHub/showBrands: when true, those items are collapsed into MORE at narrower widths
const MoreNavItem = memo(({ onClick, currentMarket, selectedCountry, showHub, showBrands }) => {
  const location = useLocation();
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const isBlogOnlyMarket = currentMarket === 'australia';
  
  // Check if we're on any of the "More" pages (include hub/brands when collapsed)
  const isMoreActive = location.pathname.includes('/blog') || 
                      location.pathname.includes('/baby-registry') ||
                      location.pathname.includes('/corporate-gifting') ||
                      location.pathname.includes('/collections') ||
                      location.pathname.includes('/my/values') ||
                      location.pathname.includes('/curator-application') ||
                      (showHub && (location.pathname.includes('/hub') || location.pathname.includes('/top'))) ||
                      (showBrands && location.pathname.includes('/brands'));
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowMoreDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <li className="nav-item-with-dropdown" ref={dropdownRef}>
      <div className="nav-dropdown-trigger">
        <button
          className={`nav-more-button ${isMoreActive ? 'active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowMoreDropdown(!showMoreDropdown);
          }}
        >
          MORE
          <span className="dropdown-arrow-small">▼</span>
        </button>
      </div>
      {showMoreDropdown && (
        <div className="nav-dropdown-menu">
          {/* Collapsed Brands - shown when screen is narrow */}
          {!isBlogOnlyMarket && showBrands && (
            <Link
              to={currentMarket === 'global' ? '/global/brands' : `/country/${selectedCountry}/brands`}
              className="nav-dropdown-item"
              onClick={() => {
                setShowMoreDropdown(false);
                onClick();
              }}
            >
              Brands
            </Link>
          )}
          {/* Collapsed Hub - shown when screen is narrow */}
          {!isBlogOnlyMarket && showHub && (
            <Link
              to={currentMarket === 'global' ? '/global/hub' : `/country/${selectedCountry}/hub`}
              className="nav-dropdown-item"
              onClick={() => {
                setShowMoreDropdown(false);
                onClick();
              }}
            >
              Hub
            </Link>
          )}
          {/* Divider when collapsed items are present */}
          {!isBlogOnlyMarket && (showBrands || showHub) && <div className="nav-dropdown-divider" />}
          {!isBlogOnlyMarket && (
            <Link
              to={currentMarket === 'global' ? '/global/baby-registry' : `/country/${selectedCountry}/baby-registry`}
              className="nav-dropdown-item"
              onClick={() => {
                setShowMoreDropdown(false);
                onClick();
              }}
            >
              Baby Registry
            </Link>
          )}
          {!isBlogOnlyMarket && (
            <Link
              to={currentMarket === 'global' ? '/global/collections' : `/country/${selectedCountry}/collections`}
              className="nav-dropdown-item"
              onClick={() => {
                setShowMoreDropdown(false);
                onClick();
              }}
            >
              My Collections
            </Link>
          )}
          {!isBlogOnlyMarket && (
            <Link
              to={currentMarket === 'global' ? '/global/baby-registry/my' : `/country/${selectedCountry}/baby-registry/my`}
              className="nav-dropdown-item"
              onClick={() => {
                setShowMoreDropdown(false);
                onClick();
              }}
            >
              My Registry
            </Link>
          )}
          {!isBlogOnlyMarket && (
            <Link
              to={currentMarket === 'global' ? '/global/my/values' : `/country/${selectedCountry}/my/values`}
              className="nav-dropdown-item"
              onClick={() => {
                setShowMoreDropdown(false);
                onClick();
              }}
            >
              My Values
            </Link>
          )}
          <Link
            to={currentMarket === 'global' ? '/global/blog' : `/country/${selectedCountry}/blog`}
            className="nav-dropdown-item"
            onClick={() => {
              setShowMoreDropdown(false);
              onClick();
            }}
          >
            Blog
          </Link>
          {!isBlogOnlyMarket && (
            <Link
              to={currentMarket === 'global' ? '/global/corporate-gifting' : `/country/${selectedCountry}/corporate-gifting`}
              className="nav-dropdown-item"
              onClick={() => {
                setShowMoreDropdown(false);
                onClick();
              }}
            >
              Gifts
            </Link>
          )}
          {!isBlogOnlyMarket && (
            <Link
              to={currentMarket === 'global' ? '/global/curator-application' : `/country/${selectedCountry}/curator-application`}
              className="nav-dropdown-item"
              onClick={() => {
                setShowMoreDropdown(false);
                onClick();
              }}
            >
              Become a Curator
            </Link>
          )}
        </div>
      )}
    </li>
  );
});

// Special HUB nav item with dropdown for Top Searches and Category Hubs (Canada only for now)
const HubNavItem = memo(({ onClick, currentMarket, selectedCountry }) => {
  const location = useLocation();
  const [showHubDropdown, setShowHubDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // Check if we're on any hub or top page
  const isHubActive = location.pathname.includes('/hub') || location.pathname.includes('/top');
  
  // Hub path for main link - both markets now have /hub
  const hubPath = currentMarket === 'global' ? '/global/hub' : `/country/${selectedCountry}/hub`;
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowHubDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Hub data for dropdown - different for each market
  const canadaHubs = [
    { title: 'Top Searches', slug: 'top', isTopSearch: true },
    { title: 'Clean Beauty', slug: 'clean-beauty' },
    { title: 'Wellness & Supplements', slug: 'wellness' },
    { title: 'Non-Toxic & Low-Waste Home', slug: 'low-waste-home' },
    { title: 'Baby & Kids', slug: 'baby-kids' },
    { title: 'Home Robots', slug: 'home-robots' },
    { title: 'Basement & Moisture Control', slug: 'basement-moisture-control' },
    { title: 'Winter Home Preparedness', slug: 'winter-home-preparedness' },
    { title: 'Sustainable Brands', slug: 'sustainable-brands' },
    { title: 'Seasonal Guides', slug: 'seasonal' }
  ];
  
  const globalHubs = [
    { title: 'Top Searches', slug: 'top', isTopSearch: true },
    { title: 'Clean Beauty & Skincare', slug: 'clean-beauty' },
    { title: 'Wellness & Supplements', slug: 'wellness' },
    { title: 'Non-Toxic Home', slug: 'non-toxic-home' },
    { title: 'Zero-Waste & Plastic-Free', slug: 'zero-waste' },
    { title: 'Baby & Kids', slug: 'baby-kids' },
    { title: 'Seasonal & Gift Guides', slug: 'seasonal' },
    { title: 'Home Robots', slug: 'home-robots' }
  ];
  
  const hubs = currentMarket === 'global' ? globalHubs : canadaHubs;
  
  // Both markets now show Hub dropdown
  return (
    <li className="nav-item-with-dropdown" ref={dropdownRef}>
      <div className="nav-dropdown-trigger">
        <NavLink
          to={hubPath}
          className={isHubActive ? 'active' : ''}
          onClick={(e) => {
            // On mobile, toggle dropdown instead of navigating
            const isMobile = window.innerWidth <= 768;
            if (isMobile) {
              e.preventDefault();
              setShowHubDropdown(!showHubDropdown);
            } else {
              onClick(e);
            }
          }}
        >
          HUB
        </NavLink>
        <button 
          className="dropdown-toggle-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowHubDropdown(!showHubDropdown);
          }}
          aria-label="Toggle hub menu"
        >
          <span className="dropdown-arrow-small">▼</span>
        </button>
      </div>
      {showHubDropdown && (
        <div className="nav-dropdown-menu nav-dropdown-menu-wide">
          {hubs.map((hub) => {
            // Generate the correct path based on market
            let linkPath;
            if (hub.isTopSearch) {
              linkPath = currentMarket === 'global' ? '/global/top' : `/country/${selectedCountry}/top`;
            } else {
              linkPath = currentMarket === 'global' ? `/global/hub/${hub.slug}` : `/country/${selectedCountry}/hub/${hub.slug}`;
            }
            
            return (
              <Link 
                key={hub.slug}
                to={linkPath}
                className="nav-dropdown-item"
                onClick={() => {
                  setShowHubDropdown(false);
                  onClick();
                }}
              >
                {hub.title}
              </Link>
            );
          })}
        </div>
      )}
    </li>
  );
});

// Special HOME nav item that handles both global and country-specific routes
const HomeNavItem = memo(({ onClick, selectedCountry, currentMarket }) => {
  const location = useLocation();
  
  // Check if we're on any home page (global or country-specific)
  const isHomeActive = location.pathname === '/global/home' || 
                       location.pathname === `/country/${selectedCountry}` ||
                       location.pathname === '/' ||
                       location.pathname.endsWith('/home');
  
  // Determine the correct home path based on current market
  const homePath = currentMarket === 'global' ? '/global/home' : `/country/${selectedCountry}`;
  
  return (
    <li>
      <NavLink
        to={homePath}
        className={isHomeActive ? 'active' : ''}
        onClick={onClick}
      >
        HOME
      </NavLink>
    </li>
  );
});

// SearchForm removed - replaced by AIDiscoveryBar component

function NavBar({ selectedCountry }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMarketSelector, setShowMarketSelector] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const { user, setUser } = useUser();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navMenuRef = useRef(null);
  const userDropdownRef = useRef(null);
  const marketSelectorRef = useRef(null);
  const hamburgerRef = useRef(null);
  const mobileBtnRef = useRef(null);

  // Track window width for progressive nav collapse
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1400);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Progressive collapse: move items into MORE as screen shrinks
  const collapseHubToMore = windowWidth < 1300;
  const collapseBrandsToMore = windowWidth < 1200;
  
  // Handle hydration
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Fetch curator handle if not already in user object
  useEffect(() => {
    const fetchCuratorHandle = async () => {
      if (user && user.email && !user.curatorHandle) {
        try {
          console.log('🔍 Fetching curator handle for email:', user.email);
          
          // First try the dedicated endpoint using main API client
          try {
            const response = await API.get(`/api/users/curator-handle?email=${encodeURIComponent(user.email)}`);
            console.log('📦 Curator handle response:', response.data);
            if (response.data.success && response.data.curatorHandle) {
              console.log('✅ Setting curatorHandle to:', response.data.curatorHandle);
              setUser({ ...user, curatorHandle: response.data.curatorHandle });
              return;
            }
          } catch (e) {
            console.log('⚠️ Dedicated endpoint not available, trying alternative...');
          }
          
          // Alternative: Query the curators API by searching for this email
          // This requires backend support or we can try to infer from email domain
          // For now, extract from domain if it's @originselect.com
          if (user.email.endsWith('@originselect.com')) {
            const handle = user.email.split('@')[0];
            console.log('✅ Using email-based handle:', handle);
            setUser({ ...user, curatorHandle: handle });
          }
        } catch (error) {
          console.log('⚠️ Could not fetch curator handle:', error);
        }
      } else {
        console.log('📊 User state:', { email: user?.email, curatorHandle: user?.curatorHandle });
      }
    };
    fetchCuratorHandle();
  }, [user, setUser]);

  // Determine current market based on path
  const currentMarket = useMemo(() => {
    if (location.pathname.startsWith('/global')) return 'global';
    if (location.pathname.startsWith('/country/canada')) return 'canada';
    if (location.pathname.startsWith('/country/australia')) return 'australia';
    if (location.pathname.startsWith('/country/usa')) return 'usa';
    return 'canada'; // default fallback
  }, [location.pathname]);

  // Get current market data
  const currentMarketData = useMemo(() =>
    MARKETS.find(market => market.id === currentMarket) || MARKETS[1], // default to Canada
    [currentMarket]
  );

  // Australia only has a blog launched so far — hide commerce-only tabs
  // (products, brands, hub, registry, collections, gifting, etc.) that don't
  // have an Australia route yet and would otherwise bounce back to Canada.
  const isBlogOnlyMarket = currentMarket === 'australia';
  
  // Memoize derived values
  const isSearchPage = useMemo(() => 
    location.pathname.includes('/search') || location.pathname.includes('/discover'),
    [location.pathname]
  );

  // Determine if the current page is the Home page
  const isHomePage = useMemo(() =>
    location.pathname.endsWith('/home') ||
    location.pathname === '/' ||
    location.pathname === `/country/${selectedCountry}`,
    [location.pathname, selectedCountry]
  );



  // Convert toggle functions to useCallback to prevent recreation on renders
  const toggleMobileMenu = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setMobileMenuOpen(prevState => {
      const newState = !prevState;
      document.body.style.overflow = newState ? 'hidden' : 'auto';
      // Add class to body for CSS targeting
      if (newState) {
        document.body.classList.add('mobile-menu-open');
      } else {
        document.body.classList.remove('mobile-menu-open');
      }
      return newState;
    });
  }, []);

  const closeMenu = useCallback((e) => {
    if (e) {
      e.stopPropagation();
    }
    setMobileMenuOpen(false);
    document.body.style.overflow = 'auto';
    document.body.classList.remove('mobile-menu-open');
  }, []);

  // Handle clicks outside navbar
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        (hamburgerRef.current && hamburgerRef.current.contains(event.target)) ||
        (mobileBtnRef.current && mobileBtnRef.current.contains(event.target))
      ) {
        return;
      }
      
      if (navMenuRef.current && !navMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
        document.body.style.overflow = 'auto';
        document.body.classList.remove('mobile-menu-open');
      }
      
      if (userDropdownRef.current && 
          !userDropdownRef.current.contains(event.target) &&
          !event.target.closest('.user-menu-button')) {
        setShowUserDropdown(false);
      }

      if (marketSelectorRef.current && 
          !marketSelectorRef.current.contains(event.target) &&
          !event.target.closest('.navbar-flag-container')) {
        setShowMarketSelector(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const logout = useCallback(() => {
    clearStoredGoogleSession();
    setUser(null);
    setShowUserDropdown(false);
  }, [setUser]);

  // Handler for favorites link - force refresh to ensure fresh data
  const handleFavoritesClick = useCallback((e) => {
    e.preventDefault();
    setShowUserDropdown(false);
    const favoritesUrl = currentMarket === 'global' 
      ? '/global/favorites' 
      : `/country/${selectedCountry}/favorites`;
    // Force full page reload to ensure fresh data
    window.location.href = favoritesUrl;
  }, [currentMarket, selectedCountry]);

  const toggleUserDropdown = useCallback(() => {
    setShowUserDropdown(prev => !prev);
  }, []);

  // Market selector handlers
  const toggleMarketSelector = useCallback(() => {
    setShowMarketSelector(prev => !prev);
  }, []);

  const handleMarketChange = useCallback((market) => {
    if (market.comingSoon) return; // Don't navigate to coming soon markets
    
    setShowMarketSelector(false);
    window.location.href = market.path; // Full page navigation to ensure proper routing
  }, []);

  // Get user initials - memoized to prevent recalculation
  const userInitials = useMemo(() => {
    if (!user || !user.name) return '';
    const names = user.name.split(' ');
    if (names.length === 1) return names[0].charAt(0);
    return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`;
  }, [user]);

  // Memoize nav items array
  const navItems = useMemo(() => {
    // Pages shown in both desktop (filtered) and mobile (full) nav
    // Desktop: home, products, brands, hub are shown directly; gifts, blog go into MORE dropdown
    // Mobile: all items shown in hamburger menu
    const pages = isBlogOnlyMarket
      ? ['home', 'blog']
      : ['home', 'products', 'brands', 'gifts', 'hub', 'developers', 'blog'];
    
    return pages.map((page) => {
      const handleNavClick = (e) => {
        e.stopPropagation();
        closeMenu();
      };
      
      // Special handling for HOME link
      if (page === 'home') {
        return (
          <HomeNavItem 
            key={page}
            onClick={handleNavClick}
            selectedCountry={selectedCountry}
            currentMarket={currentMarket}
          />
        );
      }
      
      // Special handling for BRANDS link (dropdown for global)
      if (page === 'brands') {
        return (
          <BrandsNavItem
            key={page}
            onClick={handleNavClick}
            currentMarket={currentMarket}
            selectedCountry={selectedCountry}
          />
        );
      }
      
      // Special handling for HUB link (dropdown with Top Searches + category hubs)
      if (page === 'hub') {
        return (
          <HubNavItem
            key={page}
            onClick={handleNavClick}
            currentMarket={currentMarket}
            selectedCountry={selectedCountry}
          />
        );
      }
      
      // Special handling for GIFTS link (Corporate Gifting)
      if (page === 'gifts') {
        const giftsPath = currentMarket === 'global'
          ? '/global/corporate-gifting'
          : `/country/${selectedCountry}/corporate-gifting`;
        
        return (
          <li key={page}>
            <NavLink
              to={giftsPath}
              className={location.pathname.includes('/corporate-gifting') ? 'active' : ''}
              onClick={handleNavClick}
            >
              GIFTS
            </NavLink>
          </li>
        );
      }
      
      // Special handling for DEVELOPERS link (always /global/developers)
      if (page === 'developers') {
        return (
          <li key={page}>
            <NavLink
              to="/global/developers"
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={handleNavClick}
            >
              DEVELOPERS
            </NavLink>
          </li>
        );
      }
      
      // Generic nav item (PRODUCTS, BLOG, etc.)
      const displayName = page.toUpperCase();
      const routePath = currentMarket === 'global' 
        ? `/global/${page}` 
        : `/country/${selectedCountry}/${page}`;
      
      return (
        <MemoizedNavItem 
          key={page} 
          to={routePath} 
          onClick={handleNavClick}
          selectedCountry={selectedCountry}
        >
          {displayName}
        </MemoizedNavItem>
      );
    });
  }, [selectedCountry, currentMarket, closeMenu, isBlogOnlyMarket]);
  
  // Toast message based on context
  const getToastMessage = () => {
    const isCanadianContext = currentMarket === 'canada';
    const isMobile = window.innerWidth <= 640;
    
    if (isMobile) {
      // Shorter mobile version
      return (
        <>
          <strong style={{ fontSize: '16px' }}>🎉 You're in!</strong>
          <br />
          Now you can:
          <br />
          • ❤️ Save {isCanadianContext ? 'Canadian-made ' : ''}products you love
          <br />
          • ✨ Turn favorites into Collections you can share
          <br />
          • 🎯 Save your values & see matching products
        </>
      );
    }
    
    if (isCanadianContext) {
      return (
        <>
          <strong style={{ fontSize: '16px' }}>🎉 You're in!</strong>
          <br />
          Now you can:
          <br />
          • ❤️ Save Canadian-made products you love
          <br />
          • ✨ Turn favorites into shareable Collections
          <br />
          • 🎯 Save your values & see matching products
        </>
      );
    } else {
      return (
        <>
          <strong style={{ fontSize: '16px' }}>🎉 You're in!</strong>
          <br />
          Now you can:
          <br />
          • ❤️ Save products you love
          <br />
          • ✨ Turn favorites into shareable Collections
          <br />
          • 🎯 Save your values & see matching products
        </>
      );
    }
  };
  
  // Right Section Content
  const rightSectionContent = (
    <>
      {/* Favorites Icon - Mobile only */}
      {!isBlogOnlyMarket && (
        <Link
          to={currentMarket === 'global' ? '/global/favorites' : `/country/${selectedCountry}/favorites`}
          className="mobile-favorites-icon"
          aria-label="Favorites"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        </Link>
      )}
      
      {/* Market Selector - Toggleable Flag */}
      <div className="navbar-flag-container" ref={marketSelectorRef}>
        <div onClick={toggleMarketSelector} className="navbar-flag" role="button" tabIndex={0}>
          {currentMarketData.flag.startsWith('http') ? (
            <img 
              src={currentMarketData.flag} 
              alt={`${currentMarketData.label} flag`} 
              className="navbar-flag"
              style={{ cursor: 'pointer' }}
            />
          ) : (
            <span style={{ fontSize: '18px', cursor: 'pointer', lineHeight: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
              {currentMarketData.flag}
            </span>
          )}
        </div>
        
        {/* Market Selector Dropdown */}
        <div className={`market-selector-dropdown ${showMarketSelector ? 'show' : ''}`}>
          {MARKETS.map((market) => (
            <button
              key={market.id}
              className={`market-option ${currentMarket === market.id ? 'selected' : ''} ${market.comingSoon ? 'coming-soon' : ''}`}
              onClick={() => handleMarketChange(market)}
              disabled={market.comingSoon}
            >
              <div className="market-option-flag">
                {market.flag.startsWith('http') ? (
                  <img 
                    src={market.flag} 
                    alt={`${market.label} flag`} 
                    className="market-option-flag"
                  />
                ) : (
                  <span style={{ fontSize: '14px' }}>{market.flag}</span>
                )}
              </div>
              <div className="market-option-text">
                <span className="market-option-label">{market.label}</span>
                <span className="market-option-description">{market.description}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* AI Discovery Bar - always show except on home page */}
      {!isHomePage && (
        <div className="desktop-search-bar-wrapper">
          <AIDiscoveryBar 
            variant="navbar"
            market={currentMarket}
          />
        </div>
      )}

      {user ? (
        <div className="user-menu-container">
          <button
            type="button"
            className="user-menu-button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleUserDropdown();
            }}
            aria-label="User menu"
          >
            {user.picture && !imageLoadError ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="user-avatar" 
                onError={() => setImageLoadError(true)}
              />
            ) : (
              <div className="user-initials">{userInitials}</div>
            )}
            <span className="dropdown-arrow desktop-only">▼</span>
          </button>
          
          {showUserDropdown && (
            <div className="user-dropdown" ref={userDropdownRef}>
              <div className="dropdown-user-name">{user.name}</div>
              {!isBlogOnlyMarket && (
                <>
                  <a
                    href={currentMarket === 'global' ? '/global/favorites' : `/country/${selectedCountry}/favorites`}
                    className="dropdown-menu-item"
                    onClick={handleFavoritesClick}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px'}}>
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                    </svg>
                    Favorites
                  </a>
                  <Link
                    to={currentMarket === 'global' ? '/global/collections' : `/country/${selectedCountry}/collections`}
                    className="dropdown-menu-item"
                    onClick={() => setShowUserDropdown(false)}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px'}}>
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                    My Collections
                  </Link>
                  <Link
                    to={currentMarket === 'global' ? '/global/my/values' : `/country/${selectedCountry}/my/values`}
                    className="dropdown-menu-item"
                    onClick={() => setShowUserDropdown(false)}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px'}}>
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                    </svg>
                    My Values
                  </Link>
                </>
              )}
              {/* Curator Profile - Only show if user has a curator handle (is an influencer) */}
              {user.curatorHandle && (
                <Link 
                  to={`${currentMarket === 'global' ? '/global' : `/country/${selectedCountry}`}/curators/@${user.curatorHandle}`}
                  className="dropdown-menu-item"
                  onClick={() => setShowUserDropdown(false)}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px'}}>
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  Curator Profile
                </Link>
              )}
              {/* Admin Dashboard - Only show for admin email */}
              {user.email === 'chhavi.mishra@gmail.com' && (
                <>
                  <div style={{ borderTop: '1px solid #E5E7EB', margin: '0.25rem 0' }} />
                  <Link 
                    to="/global/admin"
                    className="dropdown-menu-item"
                    onClick={() => setShowUserDropdown(false)}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px'}}>
                      <circle cx="12" cy="12" r="3"></circle>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                    </svg>
                    Admin Dashboard
                  </Link>
                  <Link 
                    to={`${currentMarket === 'global' ? '/global' : `/country/${selectedCountry}`}/ai-citations`}
                    className="dropdown-menu-item"
                    onClick={() => setShowUserDropdown(false)}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px'}}>
                      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                    </svg>
                    AI Activity
                  </Link>
                </>
              )}
              <button className="logout-button" onClick={logout}>Log out</button>
            </div>
          )}
        </div>
      ) : !isReactSnap ? ( // Conditionally render GoogleSignInButton
        // Use the lazy-loaded GoogleSignInButton component
        // This replaces the previous direct button and ensures sign-in logic is encapsulated
        <Suspense fallback={<div>Loading Sign In...</div>}>
          <GoogleSignInButton 
            setUser={setUser} 
            requireIdToken={true}
            iconOnMobile={true}
            compactMaxWidth={1420}
            onSignInSuccess={() => {
              console.log('Sign-in successful, showing toast');
              // Set the flag immediately when showing toast
              sessionStorage.setItem('hasSeenSignInToast', 'true');
              setShowToast(true);
            }}
          />
        </Suspense>
      ) : (
        <div>{/* Placeholder for GSI during prerender */}</div>
      )}
    </>
  );
  
  if (!selectedCountry) return null;

  if (isReactSnap) { // If react-snap, render a simplified version without GoogleOAuthProvider
    return (
      <>
        {/* Special mobile hamburger button - always visible on mobile */}
        <button 
          ref={mobileBtnRef}
          className={`hamburger-menu-mobile ${mobileMenuOpen ? 'menu-open' : ''}`}
          onClick={toggleMobileMenu}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          ☰
        </button>

        <header className={`navbar ${currentMarket === 'global' ? 'global-market' : ''}`}>
          <div className="navbar-container">
            {/* Logo */}
            <div className="logo-wrapper">
              <Link 
                to={currentMarket === 'global' ? '/global/home' : `/country/${selectedCountry}`}
                onClick={closeMenu}
              >
                <Logo selectedCountry={selectedCountry} isGlobalMarket={currentMarket === 'global'} />
              </Link>
            </div>

            {/* Desktop Navigation Links */}
            <nav className="desktop-nav">
              <ul>
                {/* Filter out items that go into More dropdown for desktop */}
                {navItems.filter((item) => {
                  // Remove any empty or keyless items
                  if (!item || !item.key) return false;
                  // Blog-only markets (e.g. Australia) have nothing else to put in a
                  // More dropdown, so show Blog directly instead of via that menu.
                  if (isBlogOnlyMarket) return true;
                  // Always hide gifts, top, and blog on desktop - they're in More dropdown
                  if (['gifts', 'top', 'blog'].includes(item.key)) return false;
                  // Progressive collapse: hide hub/brands at narrower widths
                  if (collapseHubToMore && item.key === 'hub') return false;
                  if (collapseBrandsToMore && item.key === 'brands') return false;
                  return true;
                })}
                {/* More dropdown - desktop only (nothing to show it for blog-only markets) */}
                {!isBlogOnlyMarket && (
                  <MoreNavItem
                    onClick={closeMenu}
                    currentMarket={currentMarket}
                    selectedCountry={selectedCountry}
                    showHub={collapseHubToMore}
                    showBrands={collapseBrandsToMore}
                  />
                )}
                {/* List Your Brand CTA */}
                {!isBlogOnlyMarket && (
                  <li className="nav-item-cta">
                    <Link
                      to={currentMarket === 'global' ? '/global/register' : `/country/${selectedCountry}/register`}
                      className="for-brands-link"
                      onClick={closeMenu}
                    >
                      LIST YOUR BRAND
                    </Link>
                  </li>
                )}
              </ul>
            </nav>

            {/* Regular Hamburger Menu Button - for tablet/desktop */}
            <div className="hamburger-wrapper">
              <button 
                ref={hamburgerRef}
                className="hamburger-button"
                onClick={toggleMobileMenu}
                aria-expanded={mobileMenuOpen}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              >
                <span className="hamburger-icon">☰</span>
              </button>
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
              <div 
                className={`menu-overlay ${mobileMenuOpen ? 'active' : ''}`} 
                onClick={toggleMobileMenu}
              ></div>
            )}

            {/* Mobile Menu */}
            <nav 
              ref={navMenuRef}
              className={`nav-menu ${mobileMenuOpen ? 'menu-open' : ''}`}
            >
              <div className="mobile-nav-header">
                <Link 
                  to={currentMarket === 'global' ? '/global/home' : `/country/${selectedCountry}`}
                  onClick={closeMenu}
                >
                  <Logo selectedCountry={selectedCountry} isGlobalMarket={currentMarket === 'global'} />
                </Link>
                <button 
                  className="close-menu-button"
                  onClick={toggleMobileMenu}
                  aria-label="Close menu"
                >
                  ✕
                </button>
              </div>

              {/* Mobile AI Discovery Bar - Always show in hamburger menu */}
              <div className="mobile-search-form-container">
                <AIDiscoveryBar 
                  variant="mobile"
                  market={currentMarket}
                  onCloseMenu={closeMenu}
                />
              </div>

              <ul>
                {navItems}
                {!isBlogOnlyMarket && (
                  <>
                    {/* Divider before user pages */}
                    <li className="mobile-menu-divider" aria-hidden="true"></li>
                    {/* My Values - matches bottom nav */}
                    <li>
                      <Link
                        to={currentMarket === 'global' ? '/global/my/values' : `/country/${selectedCountry}/my/values`}
                        onClick={closeMenu}
                        className={location.pathname.includes('/my/values') ? 'active' : ''}
                      >
                        MY VALUES
                      </Link>
                    </li>
                    {/* My Collections - matches desktop MORE */}
                    <li>
                      <Link
                        to={currentMarket === 'global' ? '/global/collections' : `/country/${selectedCountry}/collections`}
                        onClick={closeMenu}
                        className={location.pathname.includes('/collections') ? 'active' : ''}
                      >
                        MY COLLECTIONS
                      </Link>
                    </li>
                    {/* My Registry - matches desktop MORE */}
                    <li>
                      <Link
                        to={currentMarket === 'global' ? '/global/baby-registry/my' : `/country/${selectedCountry}/baby-registry/my`}
                        onClick={closeMenu}
                        className={location.pathname.includes('/baby-registry/my') ? 'active' : ''}
                      >
                        MY REGISTRY
                      </Link>
                    </li>
                    {/* Favorites - matches bottom nav */}
                    <li>
                      <Link
                        to={currentMarket === 'global' ? '/global/favorites' : `/country/${selectedCountry}/favorites`}
                        onClick={closeMenu}
                        className={location.pathname.includes('/favorites') ? 'active' : ''}
                      >
                        FAVORITES
                      </Link>
                    </li>
                    {/* Divider before info pages */}
                    <li className="mobile-menu-divider" aria-hidden="true"></li>
                    {/* Contact link - only in mobile menu */}
                    <li>
                      <Link
                        to={currentMarket === 'global' ? '/global/contact' : `/country/${selectedCountry}/contact`}
                        onClick={closeMenu}
                        className={location.pathname.includes('/contact') ? 'active' : ''}
                      >
                        CONTACT
                      </Link>
                    </li>
                    {/* About link - only in mobile menu */}
                    <li>
                      <Link
                        to={currentMarket === 'global' ? '/global/about' : `/country/${selectedCountry}/about`}
                        onClick={closeMenu}
                        className={location.pathname.includes('/about') ? 'active' : ''}
                      >
                        ABOUT
                      </Link>
                    </li>
                    {/* Become a Curator link */}
                    <li>
                      <Link
                        to={currentMarket === 'global' ? '/global/curator-application' : `/country/${selectedCountry}/curator-application`}
                        onClick={closeMenu}
                        className={location.pathname.includes('/curator-application') ? 'active' : ''}
                      >
                        BECOME A CURATOR
                      </Link>
                    </li>
                  </>
                )}
                {/* List Your Brand CTA */}
                {!isBlogOnlyMarket && (
                  <li className="mobile-cta-item">
                    <Link
                      to={currentMarket === 'global' ? '/global/register' : `/country/${selectedCountry}/register`}
                      onClick={closeMenu}
                      className="mobile-cta-button"
                    >
                      LIST YOUR BRAND
                    </Link>
                  </li>
                )}
              </ul>
            </nav>

            {/* Search Bar and User Profile - Right Side */}
            <div className="right-section">
              {/* Favorites Icon - Mobile only */}
              <Link 
                to={currentMarket === 'global' ? '/global/favorites' : `/country/${selectedCountry}/favorites`}
                className="mobile-favorites-icon"
                aria-label="Favorites"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
              </Link>
              
              {/* Market Selector - Toggleable Flag */}
              <div className="navbar-flag-container" ref={marketSelectorRef}>
                {currentMarketData && (
                  <>
                    {typeof currentMarketData.flag === 'string' && currentMarketData.flag.startsWith('http') ? (
                      <img 
                        src={currentMarketData.flag}
                        alt={`${currentMarketData.label} flag`}
                        className="navbar-flag"
                        onClick={() => setShowMarketSelector(!showMarketSelector)}
                      />
                    ) : (
                      <span 
                        className="navbar-flag"
                        onClick={() => setShowMarketSelector(!showMarketSelector)}
                        role="img"
                        aria-label={`${currentMarketData.label} flag`}
                      >
                        {currentMarketData.flag}
                      </span>
                    )}
                    
                    {/* Market Selector Dropdown */}
                    <div className={`market-selector-dropdown ${showMarketSelector ? 'show' : ''}`}>
                      {MARKETS.map(market => (
                        <button
                          key={market.id}
                          className={`market-option ${currentMarketData.id === market.id ? 'selected' : ''} ${market.comingSoon ? 'coming-soon' : ''}`}
                          onClick={() => handleMarketChange(market)}
                          disabled={market.comingSoon}
                        >
                          {typeof market.flag === 'string' && market.flag.startsWith('http') ? (
                            <img 
                              src={market.flag}
                              alt={`${market.label} flag`}
                              className="market-option-flag"
                            />
                          ) : (
                            <span 
                              className="market-option-flag"
                              role="img"
                              aria-label={`${market.label} flag`}
                            >
                              {market.flag}
                            </span>
                          )}
                          <div className="market-option-text">
                            <span className="market-option-label">{market.label}</span>
                            <span className="market-option-description">
                              {market.comingSoon ? 'Coming Soon' : market.description}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* AI Discovery Bar - always show except on home page */}
              {!isHomePage && (
                <div className="desktop-search-bar-wrapper">
                  <AIDiscoveryBar 
                    variant="navbar"
                    market={currentMarket}
                  />
                </div>
              )}
            </div>
          </div>
        </header>
      </>
    );
  }

  // Original return for non-react-snap environments
  return (
    <Suspense fallback={<div>Loading Authentication...</div>}>
      <>
          {/* Toast Notification */}
          {showToast && (
            <Toast 
              message={getToastMessage()}
              type="success"
              duration={7000}
              onClose={() => {
                setShowToast(false);
                // Flag already set in onSignInSuccess - this is just cleanup
              }}
            />
          )}
          
          {/* Special mobile hamburger button - always visible on mobile */}
          <button 
            ref={mobileBtnRef}
            className={`hamburger-menu-mobile ${mobileMenuOpen ? 'menu-open' : ''}`}
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            ☰
          </button>        <header className={`navbar ${currentMarket === 'global' ? 'global-market' : ''}`}>
          <div className="navbar-container">
            {/* Logo */}
            <div className="logo-wrapper">
              <Link 
                to={currentMarket === 'global' ? '/global/home' : `/country/${selectedCountry}`}
                onClick={closeMenu}
              >
                <Logo selectedCountry={selectedCountry} isGlobalMarket={currentMarket === 'global'} />
              </Link>
            </div>

              <nav className="desktop-nav">
                <ul>
                  {/* Filter out items that go into More dropdown for desktop */}
                  {navItems.filter((item) => {
                    // Remove any empty or keyless items
                    if (!item || !item.key) return false;
                    // Blog-only markets (e.g. Australia) have nothing else to put in a
                    // More dropdown, so show Blog directly instead of via that menu.
                    if (isBlogOnlyMarket) return true;
                    // Always hide gifts, top, and blog on desktop - they're in More dropdown
                    if (['gifts', 'top', 'blog'].includes(item.key)) return false;
                    // Progressive collapse: hide hub/brands at narrower widths
                    if (collapseHubToMore && item.key === 'hub') return false;
                    if (collapseBrandsToMore && item.key === 'brands') return false;
                    return true;
                  })}
                  {/* More dropdown - desktop only (nothing to show it for blog-only markets) */}
                  {!isBlogOnlyMarket && (
                    <MoreNavItem
                      onClick={closeMenu}
                      currentMarket={currentMarket}
                      selectedCountry={selectedCountry}
                      showHub={collapseHubToMore}
                      showBrands={collapseBrandsToMore}
                    />
                  )}
                  {/* List Your Brand CTA */}
                  {!isBlogOnlyMarket && (
                    <li className="nav-item-cta">
                      <Link
                        to={currentMarket === 'global' ? '/global/register' : `/country/${selectedCountry}/register`}
                        className="for-brands-link"
                        onClick={closeMenu}
                      >
                        LIST YOUR BRAND
                      </Link>
                    </li>
                  )}
                </ul>
              </nav>

              {/* Regular Hamburger Menu Button - for tablet/desktop */}
              <div className="hamburger-wrapper">
                <button 
                  ref={hamburgerRef}
                  className="hamburger-button"
                  onClick={toggleMobileMenu}
                  aria-expanded={mobileMenuOpen}
                  aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                >
                  <span className="hamburger-icon">☰</span>
                </button>
              </div>

              {/* Mobile Menu Overlay */}
              {mobileMenuOpen && (
                <div 
                  className={`menu-overlay ${mobileMenuOpen ? 'active' : ''}`} 
                  onClick={toggleMobileMenu}
                ></div>
              )}

              {/* Mobile Menu */}
              <nav 
                ref={navMenuRef}
                className={`nav-menu ${mobileMenuOpen ? 'menu-open' : ''}`}
              >
                <div className="mobile-nav-header">
                  <Link 
                    to={currentMarket === 'global' ? '/global/home' : `/country/${selectedCountry}`}
                    onClick={closeMenu}
                  >
                    <Logo selectedCountry={selectedCountry} isGlobalMarket={currentMarket === 'global'} />
                  </Link>
                  <button 
                    className="close-menu-button"
                    onClick={toggleMobileMenu}
                    aria-label="Close menu"
                  >
                    ✕
                  </button>
                </div>

                {/* Mobile AI Discovery Bar - Always show in hamburger menu */}
                <div className="mobile-search-form-container">
                  <AIDiscoveryBar 
                    variant="mobile"
                    market={currentMarket}
                    onCloseMenu={closeMenu}
                  />
                </div>

                <ul>
                  {navItems}
                  {!isBlogOnlyMarket && (
                    <>
                      {/* Divider before user pages */}
                      <li className="mobile-menu-divider" aria-hidden="true"></li>
                      {/* My Values - matches bottom nav */}
                      <li>
                        <Link
                          to={currentMarket === 'global' ? '/global/my/values' : `/country/${selectedCountry}/my/values`}
                          onClick={closeMenu}
                          className={location.pathname.includes('/my/values') ? 'active' : ''}
                        >
                          MY VALUES
                        </Link>
                      </li>
                      {/* My Collections - matches desktop MORE */}
                      <li>
                        <Link
                          to={currentMarket === 'global' ? '/global/collections' : `/country/${selectedCountry}/collections`}
                          onClick={closeMenu}
                          className={location.pathname.includes('/collections') ? 'active' : ''}
                        >
                          MY COLLECTIONS
                        </Link>
                      </li>
                      {/* My Registry - matches desktop MORE */}
                      <li>
                        <Link
                          to={currentMarket === 'global' ? '/global/baby-registry/my' : `/country/${selectedCountry}/baby-registry/my`}
                          onClick={closeMenu}
                          className={location.pathname.includes('/baby-registry/my') ? 'active' : ''}
                        >
                          MY REGISTRY
                        </Link>
                      </li>
                      {/* Favorites - matches bottom nav */}
                      <li>
                        <Link
                          to={currentMarket === 'global' ? '/global/favorites' : `/country/${selectedCountry}/favorites`}
                          onClick={closeMenu}
                          className={location.pathname.includes('/favorites') ? 'active' : ''}
                        >
                          FAVORITES
                        </Link>
                      </li>
                      {/* Divider before info pages */}
                      <li className="mobile-menu-divider" aria-hidden="true"></li>
                      {/* Contact link - only in mobile menu */}
                      <li>
                        <Link
                          to={currentMarket === 'global' ? '/global/contact' : `/country/${selectedCountry}/contact`}
                          onClick={closeMenu}
                          className={location.pathname.includes('/contact') ? 'active' : ''}
                        >
                          CONTACT
                        </Link>
                      </li>
                      {/* About link - only in mobile menu */}
                      <li>
                        <Link
                          to={currentMarket === 'global' ? '/global/about' : `/country/${selectedCountry}/about`}
                          onClick={closeMenu}
                          className={location.pathname.includes('/about') ? 'active' : ''}
                        >
                          ABOUT
                        </Link>
                      </li>
                      {/* Become a Curator link */}
                      <li>
                        <Link
                          to={currentMarket === 'global' ? '/global/curator-application' : `/country/${selectedCountry}/curator-application`}
                          onClick={closeMenu}
                          className={location.pathname.includes('/curator-application') ? 'active' : ''}
                        >
                          BECOME A CURATOR
                        </Link>
                      </li>
                    </>
                  )}
                  {/* List Your Brand CTA */}
                  {!isBlogOnlyMarket && (
                    <li className="mobile-cta-item">
                      <Link
                        to={currentMarket === 'global' ? '/global/register' : `/country/${selectedCountry}/register`}
                        onClick={closeMenu}
                        className="mobile-cta-button"
                      >
                        LIST YOUR BRAND
                      </Link>
                    </li>
                  )}
                </ul>
              </nav>

              {/* Search Bar and User Profile - Right Side */}
              <div className="right-section">
                {rightSectionContent}
              </div>
            </div>
          </header>
      </>
    </Suspense>
  );
}

export default memo(NavBar);
